/**
 * Korumalı rota kapısı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ÜÇ HÂL, ÜÇ FARKLI DAVRANIŞ — ve karıştırılmaları kullanıcıyı kaybettirir:
 *
 *   YÜKLENİYOR → hiçbir şey gösterme, ama EKRANI TİTRETME.
 *                Oturum kontrolü birkaç yüz milisaniye sürer; bu sürede
 *                giriş ekranını gösterip sonra içeriğe atlamak, kullanıcıya
 *                "çıkış yaptım sandım" dedirtir.
 *   OTURUMSUZ  → girişe yönlendir, AMA NEREDEN GELDİĞİNİ SAKLA.
 *   HATA       → "giriş yapın" DEME. Ağ koptuysa veya sunucu 500 verdiyse
 *                kullanıcıyı girişe atmak yanlış teşhistir; oturumu hâlâ
 *                geçerli olabilir.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { Navigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, type ReactNode } from 'react';

import { Button } from '../design/Button.tsx';
import { EmptyState, SkeletonList } from '../design/Feedback.tsx';
import { OTURUM_SONA_ERDI_KEY } from './expiry.ts';
import { useSession } from './session.ts';

export function RequireSession({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const { data: session, isPending, isError, error, refetch } = useSession();
  const yol = useRouterState({ select: (s) => s.location.href });
  const qc = useQueryClient();

  /*
   * ─── SONSUZ YÖNLENDİRME DÖNGÜSÜ (testte ölçüldü) ──────────────────────────
   * İlk yazımda `redirect` olarak GÜNCEL adres veriliyordu. Yönlendirme
   * sonrası adres `/giris` oluyor, bileşen yeni adresle tekrar çiziliyor ve
   * kendini `/giris?redirect=/giris`e yönlendiriyordu — durmadan.
   *
   * Test bunu "JavaScript heap out of memory" ile yakaladı. Tarayıcıda
   * sekmeyi kilitlerdi.
   *
   * ─── AMA "İLK ÇİZİM" DE YANLIŞ HEDEFTİ (S37 ile görünür oldu) ─────────────
   * Bu bileşen korumalı ağacın TEPESİNDE durur; kullanıcı `/sahalar → /profil`
   * gezindiğinde SÖKÜLMEZ. Hedef ilk çizimde dondurulunca, oturumu `/profil`de
   * düşen kullanıcı giriş sonrası `/sahalar`a dönerdi — çalıştığı sayfaya
   * değil. Görev 2'de bu görünmüyordu: oturum yalnız AÇILIŞTA kontrol
   * ediliyordu, ilk çizim = güncel sayfaydı.
   *
   * Doğrusu: son KORUMALI yol izlenir. `/giris` yazılmaz — döngüyü kıran
   * koruma orada.
   */
  const gelinenYol = useRef(yol);
  useEffect(() => {
    if (!yol.startsWith('/giris')) gelinenYol.current = yol;
  }, [yol]);

  if (isPending) {
    return (
      <div className="p-4">
        <SkeletonList rows={3} />
      </div>
    );
  }

  if (isError) {
    // Ağ/sunucu hatası ≠ oturumsuzluk. Kullanıcıyı girişe atmıyoruz;
    // tekrar deneme sunuyoruz. (Toplayıcıdaki geçici/kalıcı ayrımının
    // arayüzdeki karşılığı — [K-016].)
    return (
      <EmptyState
        title="Bağlantı kurulamadı"
        description={error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu.'}
        action={<Button onClick={() => void refetch()}>Tekrar dene</Button>}
      />
    );
  }

  if (session === null) {
    // Zaten giriş sayfasındaysak yönlendirme YOK — döngüyü kıran koruma.
    if (yol.startsWith('/giris')) return <></>;

    /*
     * Oturum ELDEYKEN mi düştü, hiç mi yoktu? Bayrağı `expiry.ts` koyar —
     * yani yalnız gerçekten 401 alınan bir istek varsa. Buradan çıkarım
     * yapmak ("önce vardı, şimdi yok") yanlış olurdu: kullanıcının kendi
     * bastığı ÇIKIŞ da aynı geçişi üretir ve ona "oturumunuz sona erdi"
     * demek saçmalardı.
     */
    const sonaErdi = qc.getQueryData<boolean>(OTURUM_SONA_ERDI_KEY) === true;

    // Nereden geldiğini taşı: giriş sonrası kullanıcı istediği sayfaya döner.
    return (
      <Navigate
        to="/giris"
        search={{ redirect: gelinenYol.current, ...(sonaErdi ? { sona_erdi: true } : {}) }}
        replace
      />
    );
  }

  return <>{children}</>;
}
